import React, { type ComponentProps } from 'react'

import type { AppLinkProps } from '@/components/elementary/AppLink/types'
import {
	determineLinkVariant,
	getAnchorHref,
	getLinkExceptions,
	renderContent,
} from '@/components/elementary/AppLink/utils'
import { buttonVariants } from '@/components/ui/button'
import { Link } from '@/i18n/navigation'
import { cn } from '@/lib/styles'
import { isAppLink } from '@/utils/links'

const AppLinkComponent = React.forwardRef<HTMLAnchorElement, AppLinkProps>(
	(
		{
			href,
			label,
			className,
			children,
			iconRight,
			iconLeft,
			openExternalInNewTab = false,
			forceAnchor = false,
			size = 'none',
			underline = false,
			...props
		},
		ref,
	) => {
		const variant = determineLinkVariant(href, label || children?.toString(), props.variant)
		const combinedClassName = cn(buttonVariants({ variant, size }), className)
		const linkClassName = cn(
			'group/app-link flex relative flex-row items-center gap-1 h-fit',
			underline && 'group-hover/image-link:border-b-brand-black hover:border-b-brand-black',
			variant !== 'google' && 'border-b border-transparent',
			combinedClassName,
		)

		const linkException = getLinkExceptions(
			{
				href,
				ref,
				linkClassName,
				props,
			},
			() => renderContent({ variant, iconLeft, iconRight, label, children }),
		)
		if (linkException) {
			return linkException
		}

		const isExternal = typeof href === 'string' && !isAppLink(href)
		if (isExternal || forceAnchor) {
			return (
				<a
					href={getAnchorHref(href, isExternal)}
					ref={ref}
					target={openExternalInNewTab ? '_blank' : ''}
					rel={openExternalInNewTab ? 'noopener noreferrer' : ''}
					className={linkClassName}
					{...props}
				>
					{renderContent({ variant, iconLeft, iconRight, label, children })}
				</a>
			)
		}

		return (
			<Link
				href={href as ComponentProps<typeof Link>['href']}
				ref={ref}
				className={linkClassName}
				{...props}
			>
				{renderContent({ variant, iconLeft, iconRight, label, children })}
			</Link>
		)
	},
)

const AppLinkSSRFallback = React.forwardRef<HTMLAnchorElement, AppLinkProps>((props, ref) => (
	<AppLinkComponent {...props} ref={ref} />
))

export default AppLinkSSRFallback
